import { useRef, useState, type CSSProperties, type HTMLAttributes } from 'react';

import { Button } from '../core/Button';
import { CollapsibleSection } from '../core/CollapsibleSection';
import { Icon, type IconName } from '../core/Icon';
import { SectionLabel } from '../core/SectionLabel';
import { useTranslation } from '../data/translations';

const DOUBLE_TAP_MS = 350;

export interface EmotionalStateOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
}

export interface EmotionalStateFamilyGroup<Value extends string> {
  readonly key: string;
  readonly label: string;
  readonly icon: IconName;
  /** Classe de portée de la famille (ex. `family-reparation`) pour l'accent du groupe ouvert. */
  readonly scope?: string;
  readonly states: readonly EmotionalStateOption<Value>[];
}

export interface EmotionalStateSelectorProps<Value extends string>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'onChange'> {
  readonly label?: string;
  readonly clearLabel?: string;
  readonly families: readonly EmotionalStateFamilyGroup<Value>[];
  readonly value?: Value | null;
  readonly onChange?: (value: Value | null) => void;
  /** Double-clic sur un état, ou sur l'option « Aucun état déclaré » (valeur `null`) : sélectionne
   * ET valide immédiatement, avec la valeur cliquée passée explicitement (jamais lue depuis un état
   * React potentiellement pas encore mis à jour) — le bouton Valider reste le chemin normal au
   * simple clic (voir revue « petites corrections UX »). */
  readonly onCommit?: (value: Value | null) => void;
  readonly style?: CSSProperties;
}

/**
 * Sélecteur des 36 états émotionnels, groupés par leurs 8 familles. Aucun état par défaut : le
 * mode Normal n'est pas l'état Joie, l'utilisateur peut ne rien déclarer. Un état ne porte que
 * son libellé — l'icône affichée est celle de sa famille, jamais une icône propre à l'état (les
 * 36 états n'ont pas de glyphe individuel, voir `domain/emotion/enums.ts`).
 */
export function EmotionalStateSelector<Value extends string>({
  label,
  clearLabel,
  families,
  value = null,
  onChange,
  onCommit,
  style,
  ...rest
}: EmotionalStateSelectorProps<Value>) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('state.selector.label');
  const resolvedClearLabel = clearLabel ?? t('state.clear');
  const [openFamily, setOpenFamily] = useState<string | null>(
    () => families.find((family) => family.states.some((state) => state.value === value))?.key ?? null,
  );

  /** Même mécanisme double-tap / double-clic que `ContextModeSelector` :
   *  `tapRef` mémorise la dernière valeur + timestamp, `commitGate` protège
   *  contre les doubles déclenchements (click + dblclick superposés). */
  const tapRef = useRef<{ value: Value | null; time: number } | null>(null);
  const commitGate = useRef(false);

  const handleTap = (nextValue: Value | null) => {
    if (commitGate.current) { commitGate.current = false; return; }
    onChange?.(nextValue);
    const now = Date.now();
    const prev = tapRef.current;
    if (prev && prev.value === nextValue && now - prev.time < DOUBLE_TAP_MS) {
      commitGate.current = true;
      onCommit?.(nextValue);
      tapRef.current = null;
    } else {
      tapRef.current = { value: nextValue, time: now };
    }
  };

  const handleDoubleClick = (nextValue: Value | null) => {
    if (commitGate.current) { commitGate.current = false; return; }
    commitGate.current = true;
    onCommit?.(nextValue);
  };

  return (
    <div {...rest} style={{ display: 'grid', gap: 10, ...style }}>
      <SectionLabel size={10} tone="muted">
        {resolvedLabel}
      </SectionLabel>
      <Button
        variant={value === null ? 'primary' : 'ghost'}
        size="sm"
        onClick={() => handleTap(null)}
        onDoubleClick={() => handleDoubleClick(null)}
        aria-pressed={value === null}
      >
        {resolvedClearLabel}
      </Button>
      {families.map((family) => {
        const expanded = openFamily === family.key;
        const selectedInFamily = family.states.find((state) => state.value === value);
        return (
          <CollapsibleSection
            key={family.key}
            id={`emotional-family-${family.key}`}
            label={family.label}
            summary={selectedInFamily ? selectedInFamily.label : undefined}
            expanded={expanded}
            onToggle={() => setOpenFamily(expanded ? null : family.key)}
            className={family.scope}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                padding: '0 18px 16px',
              }}
            >
              {family.states.map((state) => {
                const active = state.value === value;
                return (
                  <button
                    key={state.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => handleTap(state.value)}
                    onDoubleClick={() => handleDoubleClick(state.value)}
                    className="ai-focusable"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 34,
                      padding: '0 14px',
                      borderRadius: 'var(--r-pill)',
                      cursor: 'pointer',
                      transition: 'var(--t-hover)',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 11,
                      letterSpacing: '.04em',
                      background: active ? 'var(--accent-soft)' : 'transparent',
                      border: `1px solid ${active ? 'var(--accent-line)' : 'rgba(255,255,255,.08)'}`,
                      color: active ? 'var(--accent)' : 'var(--text-muted)',
                    }}
                  >
                    {active && <Icon name={family.icon} size={13} />}
                    {state.label}
                  </button>
                );
              })}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
}
